import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { Plus, Edit, Trash2, X, ArrowLeft, Check, Filter } from 'lucide-react';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
const TODAY_DATE = new Date().toISOString().split('T')[0];
// CSS handled by global theme.css

function DailyMilkPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [khatas, setKhatas] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View states
  const [currentView, setCurrentView] = useState('list'); // 'list', 'form'
  const [editingItem, setEditingItem] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    khata_id: '',
    date: new Date().toISOString().split('T')[0],
    milk_morning: '',
    milk_night: '',
    cow_morning: '',
    cow_night: '',
    buffalo_morning: '',
    buffalo_night: '',
    total_animals: '',
    cow_count: '',
    buffalo_count: ''
  });

  // List pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filters, setFilters] = useState({
    khata_id: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadData();
    loadKhatas();
    loadTabelas();
  }, []);

  useEffect(() => {
    loadDailyMilkRecords();
  }, [currentPage, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDailyMilkRecords(),
        loadKhatas(),
        loadTabelas()
      ]);
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyMilkRecords = async () => {
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        ...filters
      };
      const response = await apiService.getDailyMilkCollection(params);
      setData(response.data || []);
    } catch (error) {
      setError(error.message);
      console.error('Error loading daily milk records:', error);
    }
  };

  const loadKhatas = async () => {
    try {
      const response = await apiService.getKhata();
      setKhatas(response.data || []);
    } catch (error) {
      console.error('Error loading khatas:', error);
    }
  };

  const loadTabelas = async () => {
    try {
      const response = await apiService.getTabela();
      setTabelas(response.data || []);
    } catch (error) {
      console.error('Error loading tabelas:', error);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      khata_id: '',
      date: new Date().toISOString().split('T')[0],
      milk_morning: '',
      milk_night: '',
      cow_morning: '',
      cow_night: '',
      buffalo_morning: '',
      buffalo_night: '',
      total_animals: '',
      cow_count: '',
      buffalo_count: ''
    });
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      khata_id: item.khata_id || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
      milk_morning: item.milk_morning || '',
      milk_night: item.milk_night || '',
      cow_morning: item.cow_morning || '',
      cow_night: item.cow_night || '',
      buffalo_morning: item.buffalo_morning || '',
      buffalo_night: item.buffalo_night || '',
      total_animals: item.total_animals || '',
      cow_count: item.cow_count || '',
      buffalo_count: item.buffalo_count || ''
    });
    setCurrentView('form');
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this daily milk record?')) {
      try {
        await apiService.deleteDailyMilkRecord(item.produce_id);
        await loadDailyMilkRecords();
      } catch (error) {
        toast.error('Error deleting record: ' + error.message);
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.khata_id || !formData.date) {
      toast.warning('Khata and date are required fields.');
      return;
    }

    const submitData = {
      ...formData,
      khata_id: parseInt(formData.khata_id),
      milk_morning: parseFloat(formData.milk_morning) || 0,
      milk_night: parseFloat(formData.milk_night) || 0,
      cow_morning: parseFloat(formData.cow_morning) || 0,
      cow_night: parseFloat(formData.cow_night) || 0,
      buffalo_morning: parseFloat(formData.buffalo_morning) || 0,
      buffalo_night: parseFloat(formData.buffalo_night) || 0,
      total_animals: parseInt(formData.total_animals) || 0,
      cow_count: parseInt(formData.cow_count) || 0,
      buffalo_count: parseInt(formData.buffalo_count) || 0
    };

    try {
      setLoading(true);
      if (editingItem) {
        await apiService.updateDailyMilkRecord(editingItem.produce_id, submitData);
      } else {
        await apiService.createDailyMilkRecord(submitData);
      }
      
      await loadDailyMilkRecords();
      setCurrentView('list');
      setFormData({
        khata_id: '',
        date: new Date().toISOString().split('T')[0],
        milk_morning: '',
        milk_night: '',
        cow_morning: '',
        cow_night: '',
        buffalo_morning: '',
        buffalo_night: '',
        total_animals: '',
        cow_count: '',
        buffalo_count: ''
      });
      setEditingItem(null);
    } catch (error) {
      toast.error('Error saving record: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (['cow_morning', 'cow_night', 'buffalo_morning', 'buffalo_night'].includes(name)) {
        const cowM = parseFloat(next.cow_morning) || 0;
        const cowN = parseFloat(next.cow_night) || 0;
        const bufM = parseFloat(next.buffalo_morning) || 0;
        const bufN = parseFloat(next.buffalo_night) || 0;
        next.milk_morning = (cowM + bufM).toString();
        next.milk_night = (cowN + bufN).toString();
      }
      if (['cow_count', 'buffalo_count'].includes(name)) {
        const cc = parseInt(next.cow_count) || 0;
        const bc = parseInt(next.buffalo_count) || 0;
        next.total_animals = (cc + bc).toString();
      }
      return next;
    });
  };

  const handleCancel = () => {
    setCurrentView('list');
    setFormData({
      khata_id: '',
      date: new Date().toISOString().split('T')[0],
      milk_morning: '',
      milk_night: '',
      cow_morning: '',
      cow_night: '',
      buffalo_morning: '',
      buffalo_night: '',
      total_animals: '',
      cow_count: '',
      buffalo_count: ''
    });
    setEditingItem(null);
    setError(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      khata_id: '',
      start_date: '',
      end_date: ''
    });
    setCurrentPage(1);
  };

  const getKhataInfo = (khataId) => {
    const khata = khatas.find(k => parseInt(k.khata_id) === parseInt(khataId));
    if (!khata) return { khata_no: 'Unknown', tabela_name: 'Unknown' };
    
    const tabela = tabelas.find(t => parseInt(t.tabela_id) === parseInt(khata.tabela_id));
    return {
      khata_no: khata.khata_no,
      tabela_name: tabela ? tabela.name : 'Unknown'
    };
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return renderLayout(
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading records...</p>
        </div>
    );
  }

  if (error && data.length === 0) {
    return renderLayout(
        <div className="error-container">
            <h3>Error loading data</h3>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadData}>Retry</button>
        </div>
    );
  }

  // Form View
  if (currentView === 'form') {
    return renderLayout(
      <>
        <div className="page-header">
           <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
               {editingItem ? 'Edit Milk Record' : 'Add Milk Record'}
            </h1>
           </div>
        </div>

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
             <div className="form-row">
                 <div className="form-group">
                    <label htmlFor="khata_id" className="form-label">Khata *</label>
                    <select
                        id="khata_id"
                        name="khata_id"
                        className="form-select"
                        value={formData.khata_id}
                        onChange={handleInputChange}
                        disabled={loading}
                    >
                    <option value="">Select Khata</option>
                    {sortedByName(khatas, k => k.khata_no).map(khata => {
                        const tabela = tabelas.find(t => parseInt(t.tabela_id) === parseInt(khata.tabela_id));
                        return (
                        <option key={khata.khata_id} value={khata.khata_id}>
                            Khata #{khata.khata_no} - {tabela ? tabela.name : 'Unknown Tabela'}
                        </option>
                        );
                    })}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="date" className="form-label">Date *</label>
                    <input
                        type="date" max={TODAY_DATE}
                        id="date"
                        name="date"
                        className="form-input"
                        value={formData.date}
                        onChange={handleInputChange}
                        disabled={loading}
                    />
                </div>
             </div>

             <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '1rem' }}>
                <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }}>Cow Milk (Liters)</h3>
             </div>
             
             <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cow_morning" className="form-label">Morning</label>
                    <input
                        type="number"
                        id="cow_morning"
                        name="cow_morning"
                        className="form-input"
                        value={formData.cow_morning}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="cow_night" className="form-label">Evening</label>
                    <input
                        type="number"
                        id="cow_night"
                        name="cow_night"
                        className="form-input"
                        value={formData.cow_night}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
             </div>

             <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '1rem' }}>
                <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--accent-color)' }}>Buffalo Milk (Liters)</h3>
             </div>

              <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="buffalo_morning" className="form-label">Morning</label>
                    <input
                        type="number"
                        id="buffalo_morning"
                        name="buffalo_morning"
                        className="form-input"
                        value={formData.buffalo_morning}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="buffalo_night" className="form-label">Evening</label>
                    <input
                        type="number"
                        id="buffalo_night"
                        name="buffalo_night"
                        className="form-input"
                        value={formData.buffalo_night}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
             </div>
             
             <div className="form-row" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginTop: '1rem' }}>
                 <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Total Morning</label>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{parseFloat(formData.milk_morning || 0).toFixed(2)} L</div>
                 </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Total Evening</label>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>{parseFloat(formData.milk_night || 0).toFixed(2)} L</div>
                 </div>
             </div>

            <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '1rem' }}>
                <h3 className="card-title" style={{ fontSize: '1.1rem' }}>Animal Count</h3>
             </div>

            <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cow_count" className="form-label">Cows</label>
                    <input
                        type="number"
                        id="cow_count"
                        name="cow_count"
                        className="form-input"
                        value={formData.cow_count}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="buffalo_count" className="form-label">Buffalos</label>
                    <input
                        type="number"
                        id="buffalo_count"
                        name="buffalo_count"
                        className="form-input"
                        value={formData.buffalo_count}
                        onChange={handleInputChange}
                        disabled={loading}
                        placeholder="0"
                    />
                </div>
                 <div className="form-group">
                    <label htmlFor="total_animals" className="form-label">Total</label>
                    <input
                        type="number"
                        id="total_animals"
                        name="total_animals"
                        className="form-input"
                        value={formData.total_animals}
                        disabled={true}
                        placeholder="0"
                        style={{ backgroundColor: '#f1f5f9' }}
                    />
                </div>
             </div>

            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                     <Check size={18} />
                    {loading ? 'Saving...' : (editingItem ? 'Update' : 'Save Record')}
                </button>
            </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">
          Daily Milk Records
        </h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Record
        </button>
      </div>

      <div className="filter-bar">
         <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Khata</label>
             <select
                  name="khata_id"
                  className="form-select"
                  value={filters.khata_id}
                  onChange={handleFilterChange}
                  style={{ minWidth: '200px' }}
                >
                  <option value="">All Khatas</option>
                  {sortedByName(khatas, k => k.khata_no).map(khata => {
                    const tabela = tabelas.find(t => parseInt(t.tabela_id) === parseInt(khata.tabela_id));
                    return (
                      <option key={khata.khata_id} value={khata.khata_id}>
                        Khata #{khata.khata_no} - {tabela ? tabela.name : 'Unknown'}
                      </option>
                    );
                  })}
            </select>
        </div>
         <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Start Date</label>
            <input
                type="date" max={TODAY_DATE}
                name="start_date"
                className="form-input"
                value={filters.start_date}
                onChange={handleFilterChange}
            />
        </div>
         <div className="filter-group">
             <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>End Date</label>
             <input
                type="date" max={TODAY_DATE}
                name="end_date"
                className="form-input"
                value={filters.end_date}
                onChange={handleFilterChange}
            />
        </div>
        <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px', alignSelf: 'flex-end' }}>
          Clear
        </button>
      </div>

       <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Khata</th>
                  <th>Tabela</th>
                  <th>Cow (M/E)</th>
                  <th>Buffalo (M/E)</th>
                  <th>Total (M/E)</th>
                  <th>Daily Total</th>
                  <th>Animals</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(item => {
                  const khataInfo = getKhataInfo(item.khata_id);
                  const totalDaily = (parseFloat(item.milk_morning) || 0) + (parseFloat(item.milk_night) || 0);
                  
                  return (
                    <tr key={item.produce_id}>
                      <td>{new Date(item.date).toLocaleDateString('en-GB')}</td>
                      <td>#{khataInfo.khata_no}</td>
                      <td>{khataInfo.tabela_name}</td>
                      <td>{(item.cow_morning || 0).toFixed(1)} / {(item.cow_night || 0).toFixed(1)}</td>
                      <td>{(item.buffalo_morning || 0).toFixed(1)} / {(item.buffalo_night || 0).toFixed(1)}</td>
                      <td>
                          <strong>{(item.milk_morning || 0).toFixed(1)}</strong> / <strong>{(item.milk_night || 0).toFixed(1)}</strong>
                      </td>
                      <td><strong style={{ color: 'var(--primary-color)' }}>{totalDaily.toFixed(2)} L</strong></td>
                      <td>
                        <span className="status-badge available" title={`Cows: ${item.cow_count || 0}, Buffalos: ${item.buffalo_count || 0}`}>
                          {item.total_animals || 0}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button 
                            className="btn-icon"
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button 
                            className="btn-icon"
                            style={{ color: 'var(--error-color)' }}
                            onClick={() => handleDelete(item)}
                            disabled={loading}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
           {data.length === 0 && !loading && (
            <div className="no-data">
                <p>No daily milk records found</p>
            </div>
            )}
        </div>

        {data.length > 0 && (() => {
          const totals = data.reduce((acc, item) => {
            acc.cowMorn += parseFloat(item.cow_morning) || 0;
            acc.cowEve  += parseFloat(item.cow_night)   || 0;
            acc.bufMorn += parseFloat(item.buffalo_morning) || 0;
            acc.bufEve  += parseFloat(item.buffalo_night)   || 0;
            acc.morn    += parseFloat(item.milk_morning) || 0;
            acc.eve     += parseFloat(item.milk_night)   || 0;
            return acc;
          }, { cowMorn:0, cowEve:0, bufMorn:0, bufEve:0, morn:0, eve:0 });
          const total = totals.morn + totals.eve;
          return (
            <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Records</div><div style={{ fontWeight: 700 }}>{data.length}</div></div>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Cow Total (L)</div><div style={{ fontWeight: 700 }}>{(totals.cowMorn + totals.cowEve).toFixed(2)}</div></div>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Buffalo Total (L)</div><div style={{ fontWeight: 700 }}>{(totals.bufMorn + totals.bufEve).toFixed(2)}</div></div>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Morning Total (L)</div><div style={{ fontWeight: 700 }}>{totals.morn.toFixed(2)}</div></div>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Evening Total (L)</div><div style={{ fontWeight: 700 }}>{totals.eve.toFixed(2)}</div></div>
              <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Grand Total (L)</div><div style={{ fontWeight: 700, color: 'var(--primary-color)' }}>{total.toFixed(2)}</div></div>
            </div>
          );
        })()}
    </>
  );
}

export default DailyMilkPage;